import React, { createContext, useContext, useReducer, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const CartContext = createContext();

const initialState = {
  items: [],
  isOpen: false,
  reserved: {},
  inventoryBase: {},
};

function cartReducer(state, action) {
  switch (action.type) {
    case "SET_INVENTORY":
      return { ...state, inventoryBase: action.inventory };

    case "UPDATE_INVENTORY_ROW":
      return {
        ...state,
        inventoryBase: { ...state.inventoryBase, [action.flavorId]: action.available },
      };

    case "ADD_ITEM": {
      const item = action.item;
      const flavorId = item.flavorId;
      const hdNeeded = item.count / 6;
      const currentReserved = state.reserved[flavorId] ?? 0;
      const available = state.inventoryBase[flavorId] ?? 0;

      if (currentReserved + hdNeeded > available) {
        return { ...state, _error: `Sorry, not enough ${item.name} available this weekend.` };
      }

      const existing = state.items.find((i) => i.id === item.id);
      const newItems = existing
        ? state.items.map((i) => i.id === item.id ? { ...i, qty: i.qty + 1 } : i)
        : [...state.items, { ...item, qty: 1 }];

      return {
        ...state,
        items: newItems,
        reserved: {
          ...state.reserved,
          [flavorId]: currentReserved + hdNeeded,
        },
        _error: null,
      };
    }

    case "REMOVE_ITEM": {
      const item = state.items.find((i) => i.id === action.id);
      if (!item) return state;
      const hdPerBox = item.count / 6;
      return {
        ...state,
        items: state.items.filter((i) => i.id !== action.id),
        reserved: {
          ...state.reserved,
          [item.flavorId]: Math.max(0, (state.reserved[item.flavorId] ?? 0) - hdPerBox * item.qty),
        },
      };
    }

    case "UPDATE_QTY": {
      const item = state.items.find((i) => i.id === action.id);
      if (!item) return state;
      if (action.qty <= 0) {
        return cartReducer(state, { type: "REMOVE_ITEM", id: action.id });
      }
      const hdPerBox = item.count / 6;
      const delta = (action.qty - item.qty) * hdPerBox;
      const currentReserved = state.reserved[item.flavorId] ?? 0;
      const available = state.inventoryBase[item.flavorId] ?? 0;

      if (currentReserved + delta > available) {
        return { ...state, _error: `No more ${item.name} available this weekend.` };
      }

      return {
        ...state,
        items: state.items.map((i) => i.id === action.id ? { ...i, qty: action.qty } : i),
        reserved: {
          ...state.reserved,
          [item.flavorId]: currentReserved + delta,
        },
        _error: null,
      };
    }

    case "CLEAR":
      // Preserve inventoryBase so live counts survive order completion
      return {
        items: [],
        isOpen: false,
        reserved: {},
        inventoryBase: state.inventoryBase,
        _error: null,
      };

    case "CLEAR_ERROR":
      return { ...state, _error: null };

    case "TOGGLE_CART":
      return { ...state, isOpen: !state.isOpen };

    case "CLOSE_CART":
      return { ...state, isOpen: false };

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);

  const totalItems = state.items.reduce((sum, i) => sum + i.qty, 0);
  const totalPrice = state.items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const totalMacarons = state.items.reduce((sum, i) => sum + i.count * i.qty, 0);

  useEffect(() => {
    async function loadInventory() {
      const { data, error } = await supabase
        .from("inventory")
        .select("flavor_id, available");

      if (!error && data) {
        const inv = {};
        data.forEach((row) => { inv[row.flavor_id] = row.available; });
        dispatch({ type: "SET_INVENTORY", inventory: inv });
      }
      setInventoryLoaded(true);
    }

    loadInventory();

    const channel = supabase
      .channel("inventory_realtime")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "inventory" },
        (payload) => {
          dispatch({
            type: "UPDATE_INVENTORY_ROW",
            flavorId: payload.new.flavor_id,
            available: payload.new.available,
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return (
    <CartContext.Provider value={{
      ...state,
      inventoryLoaded,
      totalItems,
      totalPrice,
      totalMacarons,
      dispatch,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
